import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { parseInstant } from "../../utils/dateUtils";
import { adminService } from "../../features/admin/service";
import Pagination from "../../components/admin/Pagination";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";
import { whatsappTypeLabel } from "../../features/admin/logTypeLabels";

const sectionSpinner = (
  <div className="flex items-center justify-center py-10 text-gray-500 text-sm"><Spinner /></div>
);

type WhatsappLog = {
  id: string;
  call_type: string;
  endpoint: string;
  request_payload: Record<string, unknown>;
  status: string;
  response_body: Record<string, unknown> | null;
  error: string | null;
  campaign_id: string | null;
  phone_last4: string | null;
  delivery_status: string | null;
  delivery_error: string | null;
  delivery_updated_at: string | null;
  delivery_pending: boolean;
  created_at: string | null;
};

// Delivery cell: a real chatbase-reported status, an amber "pending" flag for
// a completed send whose callback never arrived (chatbase accepted it >15min
// ago but we never heard back — see whatsapp_routes.py `_is_pending`), or a
// plain "—" for a send that's still genuinely in flight.
function DeliveryBadge({ log }: { log: WhatsappLog }) {
  if (log.delivery_status) {
    return (
      <div className="flex flex-col items-start gap-1">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[log.delivery_status] ?? "bg-gray-700 text-gray-300"}`}>
          {log.delivery_status}
        </span>
        {log.delivery_error && (
          <span className="text-xs text-red-300 max-w-[16rem] truncate" title={log.delivery_error}>
            {log.delivery_error}
          </span>
        )}
      </div>
    );
  }
  if (log.delivery_pending) {
    return (
      <span
        className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400"
        title="Chatbase accepted this call but never sent a delivery callback — likely a callback-URL/config issue, not a slow message."
      >
        pending
      </span>
    );
  }
  return <span className="text-gray-600">—</span>;
}

// One call's summary row + its click-to-expand payload/response detail row.
// Reused both for a standalone (ungrouped) call and for each call nested
// inside an expanded same-number group.
function CallRow({
  log, expanded, onToggle, indent,
}: { log: WhatsappLog; expanded: boolean; onToggle: () => void; indent?: boolean }) {
  return (
    <Fragment>
      <tr
        onClick={onToggle}
        className={`border-b border-gray-800 last:border-0 cursor-pointer hover:bg-gray-800/50 ${indent ? "bg-gray-950/40" : ""}`}
      >
        <td className={`px-4 py-3 text-gray-400 whitespace-nowrap ${indent ? "pl-8" : ""}`}>{fmtDate(log.created_at)}</td>
        <td className="px-4 py-3 text-gray-300">
          {whatsappTypeLabel(log.call_type)}
          <span className="block text-xs text-gray-500 font-mono">{log.call_type}</span>
        </td>
        <td className="px-4 py-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[log.status] ?? "bg-gray-700 text-gray-300"}`}>
            {log.status}
          </span>
        </td>
        <td className="px-4 py-3">
          <DeliveryBadge log={log} />
        </td>
        <td className="px-4 py-3 text-gray-400">{log.phone_last4 ? `•••${log.phone_last4}` : "—"}</td>
        <td className="px-4 py-3 text-gray-400">{log.campaign_id ?? "—"}</td>
        <td className="px-4 py-3 text-gray-500">{expanded ? "▲" : "▼"}</td>
      </tr>
      {expanded && (
        <tr className="border-b border-gray-800 last:border-0 bg-gray-950">
          <td colSpan={7} className="px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Endpoint</p>
                <p className="text-xs text-gray-300 break-all mb-3">{log.endpoint}</p>
                <p className="text-xs text-gray-500 mb-1">Data sent</p>
                <pre className="text-xs text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-3 overflow-x-auto">
                  {JSON.stringify(log.request_payload, null, 2)}
                </pre>
              </div>
              <div>
                {log.error && (
                  <>
                    <p className="text-xs text-red-400 mb-1">Error</p>
                    <p className="text-xs text-red-300 mb-3">{log.error}</p>
                  </>
                )}
                {log.delivery_status && (
                  <>
                    <p className="text-xs text-gray-500 mb-1">Delivery</p>
                    <p className="text-xs text-gray-300 mb-3">
                      {log.delivery_status}
                      {log.delivery_updated_at && ` · ${fmtDate(log.delivery_updated_at)}`}
                      {log.delivery_error && <span className="block text-red-300 mt-0.5">{log.delivery_error}</span>}
                    </p>
                  </>
                )}
                <p className="text-xs text-gray-500 mb-1">Response</p>
                <pre className="text-xs text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-3 overflow-x-auto">
                  {JSON.stringify(log.response_body ?? {}, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// "completed"/"failed" filter the API call; "delivery_failed" filters on the
// callback-reported delivery status — chatbase can accept a send (completed)
// and still fail to deliver it, e.g. when the sending number hits its 24h cap.
// "delivery_pending" is a completed send with no delivery callback at all
// after the wait window — almost always a callback-URL/config problem.
type LogFilter = "" | "completed" | "failed" | "delivery_failed" | "delivery_pending";
const FILTERS: { key: LogFilter; label: string }[] = [
  { key: "", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
  { key: "delivery_failed", label: "Delivery failed" },
  { key: "delivery_pending", label: "Delivery pending" },
];

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-emerald-500/10 text-emerald-400",
  failed: "bg-red-500/10 text-red-400",
  undelivered: "bg-red-500/10 text-red-400",
  sent: "bg-blue-500/10 text-blue-400",
  delivered: "bg-emerald-500/10 text-emerald-400",
  read: "bg-emerald-500/10 text-emerald-400",
  queued: "bg-gray-500/10 text-gray-400",
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  const d = parseInstant(iso) ?? new Date(iso);
  return d.toLocaleString();
}

const AdminWhatsAppPage: React.FC = () => {
  const [summary, setSummary] = useState<{ completed: number; failed: number; delivery_failed: number; delivery_pending: number; total: number } | null>(null);
  const [logs, setLogs] = useState<WhatsappLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState("");
  const { showError } = useAdminToast();
  useEffect(() => { if (logsError) showError(logsError); }, [logsError, showError]);
  const [statusFilter, setStatusFilter] = useState<LogFilter>("");
  const [typeFilter, setTypeFilter] = useState("");
  const [types, setTypes] = useState<{ call_type: string; count: number }[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(8);

  const [health, setHealth] = useState<{ ok: boolean; data?: Record<string, unknown>; error?: string } | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const checkHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      setHealth(await adminService.getChatbaseHealth());
    } catch (err) {
      console.error(err);
      setHealth({ ok: false, error: "Request to /api/admin/whatsapp/health failed" });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => { void checkHealth(); }, [checkHealth]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError("");
    try {
      const [logsRes, summaryRes, typesRes] = await Promise.all([
        adminService.getWhatsappLogs({
          status: statusFilter === "delivery_failed" || statusFilter === "delivery_pending" ? undefined : statusFilter || undefined,
          delivery_status: statusFilter === "delivery_failed" ? "failed" : statusFilter === "delivery_pending" ? "pending" : undefined,
          call_type: typeFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          limit: pageSize,
          skip: page * pageSize,
        }),
        adminService.getWhatsappLogsSummary(),
        adminService.getWhatsappLogTypes(),
      ]);
      setLogs(logsRes.items);
      setTotal(logsRes.total);
      setSummary(summaryRes);
      setTypes(typesRes.items);
    } catch (err) {
      console.error(err);
      setLogsError("Failed to load WhatsApp call logs");
    } finally {
      setLogsLoading(false);
    }
  }, [statusFilter, typeFilter, dateFrom, dateTo, page, pageSize]);

  useEffect(() => { void loadLogs(); }, [loadLogs]);
  useEffect(() => { setPage(0); }, [pageSize]);
  useEffect(() => { setExpandedGroup(null); setExpanded(null); }, [logs]);

  // Same number can fire several calls in quick succession (OTP retries,
  // contact/lead sync + template send, …) — group them under one row instead
  // of one row per call so the table doesn't balloon with duplicate numbers.
  // Rows with no phone_last4 (e.g. lead/contact upserts) stay ungrouped —
  // grouping unknowns together would misleadingly merge unrelated calls.
  const groups = useMemo(() => {
    const order: string[] = [];
    const byKey = new Map<string, WhatsappLog[]>();
    logs.forEach((log) => {
      const key = log.phone_last4 ? `phone:${log.phone_last4}` : `single:${log.id}`;
      if (!byKey.has(key)) {
        byKey.set(key, []);
        order.push(key);
      }
      byKey.get(key)!.push(log);
    });
    return order.map((key) => ({ key, logs: byKey.get(key)! }));
  }, [logs]);

  const handleRefreshAll = useCallback(() => {
    return Promise.all([checkHealth(), loadLogs()]).then(() => {});
  }, [checkHealth, loadLogs]);

  return (
    <div className="p-6 max-w-5xl">
      <AdminPageHeader
        title="WhatsApp Call Log"
        subtitle="Monitor every outbound WhatsApp message sent via chatbase (contact/lead sync)."
        actions={<RefreshButton onRefresh={handleRefreshAll} />}
      />

      {/* Chatbase connection health */}
      <div className="mb-8 flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 max-w-2xl">
        <span
          className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
            healthLoading ? "bg-gray-500 animate-pulse" : health?.ok ? "bg-emerald-500" : "bg-red-500"
          }`}
        />
        <div className="flex-1">
          <p className="text-sm text-gray-300">
            Chatbase connection: {healthLoading ? "checking…" : health?.ok ? "healthy" : "unreachable"}
          </p>
          {health && !health.ok && (
            <p className="text-xs text-red-400 mt-0.5">{health.error}</p>
          )}
        </div>
        <button
          onClick={checkHealth}
          disabled={healthLoading}
          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-700 text-gray-300 hover:text-white disabled:opacity-50"
        >
          Recheck
        </button>
      </div>

      {/* Call logs */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h2 className="text-lg font-semibold text-white">Chatbase Call Log</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Filter by type"
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              className="px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-900 border border-gray-800 text-gray-300"
            >
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t.call_type} value={t.call_type}>
                  {whatsappTypeLabel(t.call_type)} · {t.call_type} ({t.count})
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <input
                type="date"
                aria-label="From date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                className="px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-900 border border-gray-800 text-gray-300"
              />
              <span className="text-gray-600 text-xs">to</span>
              <input
                type="date"
                aria-label="To date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                className="px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-900 border border-gray-800 text-gray-300"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); setPage(0); }}
                  className="px-2 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white"
                  title="Clear date range"
                >
                  ✕
                </button>
              )}
            </div>
            {FILTERS.map(({ key, label }) => (
              <button
                key={key || "all"}
                onClick={() => { setStatusFilter(key); setPage(0); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  statusFilter === key
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-gray-900 border-gray-800 text-gray-400 hover:text-white"
                }`}
              >
                {label}
                {summary && key !== "" && ` (${summary[key]})`}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Every outbound call to chatbase (WhatsApp sends, contact/lead sync). OTP codes are type{" "}
          <code>otp_whatsapp</code> (older OTP rows show as <code>send_template_message</code>). Status is whether
          chatbase accepted the request; Delivery is what actually happened to the message, reported back via the
          delivery callback.
        </p>

        {logsError && <p className="text-sm text-red-400 mb-3">{logsError}</p>}

        {logsLoading ? sectionSpinner : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500 text-xs uppercase">
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Delivery</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No calls logged yet.</td>
                  </tr>
                )}
                {groups.map((group) => {
                  if (group.logs.length === 1) {
                    const log = group.logs[0];
                    return (
                      <CallRow
                        key={group.key}
                        log={log}
                        expanded={expanded === log.id}
                        onToggle={() => setExpanded(expanded === log.id ? null : log.id)}
                      />
                    );
                  }

                  const latest = group.logs[0];
                  const isGroupOpen = expandedGroup === group.key;
                  const distinctTypes = new Set(group.logs.map((l) => l.call_type));
                  const failedCount = group.logs.filter((l) => l.status === "failed").length;
                  const pendingCount = group.logs.filter((l) => l.delivery_pending).length;

                  return (
                    <Fragment key={group.key}>
                      <tr
                        onClick={() => setExpandedGroup(isGroupOpen ? null : group.key)}
                        className="border-b border-gray-800 last:border-0 cursor-pointer hover:bg-gray-800/50"
                      >
                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{fmtDate(latest.created_at)}</td>
                        <td className="px-4 py-3 text-gray-300">
                          {distinctTypes.size === 1 ? whatsappTypeLabel(latest.call_type) : "Multiple"}
                          <span className="block text-xs text-gray-500 font-mono">
                            {distinctTypes.size === 1 ? latest.call_type : `${distinctTypes.size} types`}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[latest.status] ?? "bg-gray-700 text-gray-300"}`}>
                            {latest.status}
                          </span>
                          {failedCount > 0 && (
                            <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                              {failedCount} failed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <DeliveryBadge log={latest} />
                          {pendingCount > 0 && !latest.delivery_pending && (
                            <span
                              className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400"
                              title="Another call in this group never got a delivery callback"
                            >
                              {pendingCount} pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-400">
                          {latest.phone_last4 ? `•••${latest.phone_last4}` : "—"}
                          <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-300">
                            {group.logs.length} calls
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400">{latest.campaign_id ?? "—"}</td>
                        <td className="px-4 py-3 text-gray-500">{isGroupOpen ? "▲" : "▼"}</td>
                      </tr>
                      {isGroupOpen && group.logs.map((log) => (
                        <CallRow
                          key={log.id}
                          log={log}
                          expanded={expanded === log.id}
                          onToggle={() => setExpanded(expanded === log.id ? null : log.id)}
                          indent
                        />
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div className="border-t border-gray-800">
              <Pagination
                variant="panel"
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWhatsAppPage;
